import React, { useState, useRef } from "react";
import axios from "axios";
import { Download, Copy, CheckCheck, RefreshCw, ImagePlus, X } from "lucide-react";
import { useLanguage } from "../App";

const API = `/api`;

const ASPECT_RATIOS = [
  { value: "16:9", labelKey: "ar_landscape", w: 64, h: 36 },
  { value: "9:16", labelKey: "ar_portrait",  w: 36, h: 64 },
  { value: "1:1",  labelKey: "ar_square",    w: 50, h: 50 },
];

export default function ImageGenerator() {
  const { t } = useLanguage();

  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refImage, setRefImage] = useState(null);
  const [refPreview, setRefPreview] = useState(null);
  const fileInputRef = useRef(null);

  const suggestions = [t("img_sug1"), t("img_sug2"), t("img_sug3"), t("img_sug4")];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.split(",")[1];
      setRefImage(b64);
      setRefPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeRef = () => {
    setRefImage(null);
    setRefPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError(t("error_no_prompt"));
      return;
    }
    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const res = await axios.post(`${API}/generate/image`, {
        prompt: prompt.trim(),
        style: style.trim() || undefined,
        aspect_ratio: aspectRatio,
        reference_image: refImage || undefined,
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("error_generation"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const link = document.createElement("a");
    link.href = `data:${result.mime_type};base64,${result.image}`;
    link.download = `khatira-image-${Date.now()}.png`;
    link.click();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setPrompt("");
    setStyle("");
  };

  return (
    <div className="fade-in-up" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* Step 1 — Prompt */}
      <div className="step-card">
        <StepLabel number="01" title={t("img_step1_title")} />

        <div style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.22em", color: "rgba(201,168,76,0.5)", textTransform: "uppercase", marginBottom: "0.65rem" }}>
            {t("img_suggestions_label")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {suggestions.map((s) => (
              <button key={s} className="suggestion-chip" data-testid="img-suggestion-chip" onClick={() => setPrompt(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 400, color: "#B8956A", letterSpacing: "0.08em", display: "block", marginBottom: "0.5rem" }}>
          {t("img_prompt_label")}
        </label>
        <textarea
          className="khatira-textarea"
          data-testid="img-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t("img_prompt_placeholder")}
          style={{ minHeight: "110px" }}
        />
      </div>

      {/* Step 2 — Style (Optional) */}
      <div className="optional-section">
        <CollapsibleSection title={t("img_style_label")} optionalLabel={t("optional")}>
          <textarea
            className="khatira-textarea"
            data-testid="img-style-input"
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            placeholder={t("img_style_placeholder")}
            style={{ minHeight: "72px" }}
          />
        </CollapsibleSection>
      </div>

      {/* Reference Image Upload */}
      <div className="optional-section">
        <CollapsibleSection title={t("ref_image_label")} optionalLabel={t("optional")}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: "rgba(184,149,106,0.6)", marginBottom: "0.75rem", fontWeight: 300 }}>
            {t("ref_image_desc")}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
            data-testid="img-ref-upload-input"
          />
          {refPreview ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <img src={refPreview} alt="Reference" style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "2px", border: "1px solid rgba(201,168,76,0.2)" }} />
              <button data-testid="img-ref-remove" onClick={removeRef} style={{ background: "none", border: "1px solid rgba(180,60,40,0.3)", borderRadius: "2px", color: "#D4836A", cursor: "pointer", padding: "0.35rem 0.7rem", fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <X size={12} /> {t("ref_image_remove")}
              </button>
            </div>
          ) : (
            <button
              data-testid="img-ref-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              style={{ background: "rgba(201,168,76,0.06)", border: "1px dashed rgba(201,168,76,0.25)", borderRadius: "2px", padding: "1.5rem", cursor: "pointer", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", color: "#B8956A", fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem" }}
            >
              <ImagePlus size={24} style={{ color: "rgba(201,168,76,0.4)" }} />
              {t("ref_image_label")}
            </button>
          )}
        </CollapsibleSection>
      </div>

      {/* Step 3 — Aspect Ratio */}
      <div className="step-card">
        <StepLabel number="02" title={t("img_aspect_label")} />
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.value}
              data-testid={`img-ar-${ar.value.replace(":", "x")}`}
              className={`ar-btn ${aspectRatio === ar.value ? "active" : ""}`}
              onClick={() => setAspectRatio(ar.value)}
            >
              <div className="ar-icon" style={{ width: ar.w * 0.7, height: ar.h * 0.7 }} />
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", color: aspectRatio === ar.value ? "#D4B878" : "#B8956A", fontWeight: 400 }}>
                {ar.value}
              </span>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", color: "rgba(184,149,106,0.55)" }}>
                {t(ar.labelKey)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          data-testid="img-error"
          style={{
            padding: "1rem 1.25rem",
            background: "rgba(180,60,40,0.08)",
            border: "1px solid rgba(180,60,40,0.25)",
            borderRadius: "2px",
            color: "#D4836A",
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.88rem",
          }}
        >
          {error}
        </div>
      )}

      {/* Generate Button */}
      {!result && (
        <button
          data-testid="generate-image-btn"
          className="btn-primary"
          style={{ padding: "1rem", fontSize: "0.85rem", letterSpacing: "0.18em", width: "100%", textTransform: "uppercase" }}
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem" }}>
              <span className="loading-ring spin-slow" style={{ width: 18, height: 18, borderWidth: 1.5 }} />
              {t("img_generating")}
            </span>
          ) : (
            t("img_generate_btn")
          )}
        </button>
      )}

      {/* Output */}
      {result && (
        <div className="step-card fade-in-up" data-testid="img-output-section">
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,168,76,0.55)", marginBottom: "1.25rem" }}>
            {t("out_image_ready")}
          </p>

          <div className="image-output-wrapper" style={{ marginBottom: "1.5rem" }}>
            <img
              data-testid="generated-image"
              src={`data:${result.mime_type};base64,${result.image}`}
              alt="Generated"
              style={{ width: "100%", display: "block", borderRadius: "2px" }}
            />
            <div className="img-overlay">
              <button
                className="btn-primary"
                style={{ padding: "0.6rem 1.2rem", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "0.4rem" }}
                onClick={handleDownload}
              >
                <Download size={13} /> {t("out_download")}
              </button>
            </div>
          </div>

          {/* Prompt Summary */}
          <div style={{ background: "#120904", borderRadius: "2px", padding: "1rem 1.25rem", marginBottom: "1.25rem", border: "1px solid rgba(201,168,76,0.1)" }}>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,168,76,0.5)", marginBottom: "0.5rem" }}>
              {t("out_prompt_summary")}
            </p>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", color: "#B8956A", lineHeight: 1.65, fontWeight: 300 }}>
              {result.prompt}
            </p>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button
              data-testid="download-image-btn"
              className="btn-primary"
              style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
              onClick={handleDownload}
            >
              <Download size={13} /> {t("out_download")}
            </button>
            <button
              data-testid="copy-prompt-btn"
              className="btn-ghost"
              style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
              onClick={handleCopy}
            >
              {copied ? <CheckCheck size={13} /> : <Copy size={13} />}
              {copied ? t("out_copied") : t("out_copy")}
            </button>
            <button
              data-testid="generate-another-btn"
              className="btn-ghost"
              style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: "0.5rem" }}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              <RefreshCw size={13} /> {t("out_another")}
            </button>
            <button
              data-testid="new-project-btn"
              className="btn-ghost"
              style={{ padding: "0.7rem 1.5rem", fontSize: "0.82rem" }}
              onClick={handleReset}
            >
              {t("out_new")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */
function StepLabel({ number, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
      <span
        style={{
          fontFamily: "'Cinzel', serif",
          fontSize: "1.8rem",
          fontWeight: 400,
          color: "rgba(201,168,76,0.25)",
          lineHeight: 1,
          minWidth: "2rem",
        }}
      >
        {number}
      </span>
      <h3
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.95rem",
          fontWeight: 500,
          color: "#EDE0C4",
          margin: 0,
          letterSpacing: "0.04em",
        }}
      >
        {title}
      </h3>
    </div>
  );
}

function CollapsibleSection({ title, optionalLabel, children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="optional-trigger"
        onClick={() => setOpen(!open)}
        data-testid={`collapsible-${title}`}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          {title}
          <span style={{ fontSize: "0.62rem", letterSpacing: "0.18em", color: "rgba(184,149,106,0.4)", textTransform: "uppercase", fontWeight: 300 }}>
            {optionalLabel}
          </span>
        </span>
        <span style={{ transition: "transform 0.25s ease", transform: open ? "rotate(45deg)" : "rotate(0deg)", fontSize: "1.1rem", lineHeight: 1, color: "rgba(201,168,76,0.5)" }}>
          +
        </span>
      </button>
      {open && <div className="optional-body">{children}</div>}
    </>
  );
}
